import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAppStore } from '@/store/useAppStore';
import type { Theme } from '@/types';
import {
  applyThemeToDocument,
  resolveTheme,
  subscribeToSystemTheme,
  toggleThemeValue,
  type ResolvedTheme,
} from '@/theme/theme';

function subscribeToStore(callback: () => void) {
  return useAppStore.subscribe((state) => state.theme, callback);
}

function getThemeSnapshot(): Theme {
  return useAppStore.getState().theme;
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribeToStore, getThemeSnapshot, () => 'dark' as Theme);
  const setThemeStore = useAppStore((state) => state.setTheme);
  const resolvedTheme: ResolvedTheme = resolveTheme(theme);

  useEffect(() => {
    applyThemeToDocument(resolvedTheme);
    return subscribeToSystemTheme(theme, (next) => {
      applyThemeToDocument(next);
    });
  }, [theme, resolvedTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeStore(next);
    },
    [setThemeStore]
  );

  const toggleTheme = useCallback(() => {
    setThemeStore(toggleThemeValue(theme));
  }, [theme, setThemeStore]);

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
  };
}

/** Imperative toggle for command palette / keyboard shortcuts (non-React) */
export function toggleThemeGlobal(): void {
  const current = useAppStore.getState().theme;
  useAppStore.getState().setTheme(toggleThemeValue(current));
}
